import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  Form,
  Select,
  InputNumber,
  Button,
  Typography,
  Spin,
  message,
  Space,
  Switch,
  Checkbox,
  Divider,
  Result,
  Modal,
  Radio
} from 'antd'
import {
  UserOutlined,
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SaveOutlined,
  CopyOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import { startCommissionTour } from '../utils/appTour'
import { now as tzNow } from '../utils/timezone'

const { Title, Paragraph, Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(213, 114, 138, 0.12);
  border: 1px solid rgba(213, 114, 138, 0.08);

  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    color: #8c8c8c;
    font-size: 14px;
  }
`

const FormWrapper = styled(Form)`
  margin-top: 16px;

  .ant-form-item {
    margin-bottom: 20px;
  }
`

const TierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff5f7;
  border: 1px solid #f4d6de;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
`

const MaterialCostBlock = styled.div`
  background: #f9f0f5;
  border: 1px solid #f4d6de;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`

const LabelText = styled.span`
  color: #576079;
  font-weight: 600;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const InfoCard = styled(Card)`
  border-radius: 16px;
  background: #ffffffcc;
  border: 1px dashed #f0ccd4;
`

const EmployeeOptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EmptyHint = styled.div`
  padding: 16px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  color: #8c8c8c;
  text-align: center;
`

const MIN_TIERS = 1

const normalizeCommissionData = (payload) => {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.tiers)) return payload.tiers
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.records)) return payload.records
  if (payload?.threshold !== undefined || payload?.rate !== undefined) {
    return [payload]
  }
  return []
}

const EmployeeCommissionSettingsPage = () => {
  const { user, businessPlan } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [form] = Form.useForm()
  const [employees, setEmployees] = useState([])
  const [loadingEmployees, setLoadingEmployees] = useState(false)
  const [fetchingCommission, setFetchingCommission] = useState(false)
  const [saving, setSaving] = useState(false)

  const businessId = user?.businessId

  const fetchEmployees = useCallback(async () => {
    if (!businessId) return
    try {
      setLoadingEmployees(true)
      const response = await api.get(`/api/employees/${businessId}`)
      const employeesData = Array.isArray(response.data) ? response.data : []
      setEmployees(employeesData)
    } catch (error) {
      console.error('取得員工清單失敗', error)
      message.error('無法載入員工清單，請稍後再試')
    } finally {
      setLoadingEmployees(false)
    }
  }, [businessId])

  const fetchEmployeeCommission = useCallback(
    async (employeeId) => {
      if (!businessId || !employeeId) return
      try {
        setFetchingCommission(true)
        const response = await api.get(
          `/api/businesses/${businessId}/employees/${employeeId}/commission`
        )
        const commissionPayload =
          response.data?.data ??
          response.data?.commission ??
          response.data ??
          []
        const tiers = normalizeCommissionData(
          commissionPayload?.tiers || commissionPayload
        )
        const deductMaterialCost =
          commissionPayload?.deductMaterialCost === true
        const ladderIncludesProduct =
          commissionPayload?.ladderIncludesProduct !== false
        // 「總業績」納入哪些業績類別（後端已解析好；舊資料以 ladderIncludesProduct 推導）
        const tpi = commissionPayload?.tierPerformanceItems || {}
        const includeProduct =
          typeof tpi.product === 'boolean' ? tpi.product : ladderIncludesProduct
        const includeValueAdded =
          typeof tpi.valueAdded === 'boolean'
            ? tpi.valueAdded
            : ladderIncludesProduct
        const includeClassTicket =
          typeof tpi.classTicket === 'boolean' ? tpi.classTicket : false
        const classTicketRate =
          commissionPayload?.classTicketRate === null ||
          commissionPayload?.classTicketRate === undefined
            ? null
            : Number(commissionPayload.classTicketRate)
        const commissionRule =
          commissionPayload?.commissionRule === 'profit_rate_minus_fee'
            ? 'profit_rate_minus_fee'
            : 'sales_rate'
        const raw = commissionPayload?.materialCostDefault ?? commissionPayload?.materialCost
        const materialCostType = raw?.type === 'amount' ? 'amount' : 'percentage'
        const materialCostValue =
          materialCostType === 'amount'
            ? Number(raw?.amount ?? raw?.value ?? 0)
            : Number(raw?.percentage ?? raw?.value ?? 0)
        if (tiers.length > 0) {
          form.setFieldsValue({
            deductMaterialCost,
            ladderIncludesProduct,
            includeProduct,
            includeValueAdded,
            includeClassTicket,
            classTicketRate,
            commissionRule,
            materialCostType,
            materialCostValue,
            tiers: tiers.map((tier) => ({
              threshold: Number(tier.threshold ?? tier.minAmount ?? 0),
              rate: Number(tier.rate ?? tier.percentage ?? tier.percent ?? 0)
            }))
          })
        } else {
          form.setFieldsValue({
            deductMaterialCost,
            ladderIncludesProduct,
            includeProduct,
            includeValueAdded,
            includeClassTicket,
            classTicketRate,
            commissionRule,
            materialCostType,
            materialCostValue,
            tiers: [{ threshold: 0, rate: 0 }]
          })
        }
      } catch (error) {
        console.error('取得抽成設定失敗', error)
        form.setFieldsValue({
          deductMaterialCost: false,
          ladderIncludesProduct: true,
          includeProduct: true,
          includeValueAdded: true,
          includeClassTicket: false,
          classTicketRate: null,
          commissionRule: 'sales_rate',
          materialCostType: 'percentage',
          materialCostValue: 0,
          tiers: [{ threshold: 0, rate: 0 }]
        })
      } finally {
        setFetchingCommission(false)
      }
    },
    [businessId, form]
  )

  useEffect(() => {
    if (!hasFeature('salary')) return
    fetchEmployees()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, hasFeature])

  // 所有 Hooks 必須在權限檢查之前
  const employeeOptions = useMemo(() => {
    return employees.map((employee) => {
      const displayName = employee.name || employee.username || '未命名員工'
      const roleText = employee.businessRole
        ? `（${employee.businessRole}）`
        : ''
      const searchText = [
        displayName,
        employee.username,
        employee.email,
        employee.phone,
        employee.businessRole
      ]
        .filter(Boolean)
        .join(' ')

      return {
        value: employee._id,
        node: (
          <EmployeeOptionLabel>
            <UserOutlined style={{ color: '#d5728a' }} />
            <span>
              {displayName}
              {roleText}
            </span>
          </EmployeeOptionLabel>
        ),
        searchText
      }
    })
  }, [employees])

  const handleEmployeeChange = useCallback(
    (employeeId) => {
      form.setFieldsValue({ tiers: undefined })
      if (employeeId) {
        fetchEmployeeCommission(employeeId)
      }
    },
    [fetchEmployeeCommission, form]
  )

  const [applyToOthersVisible, setApplyToOthersVisible] = useState(false)
  const [applyToOthersLoading, setApplyToOthersLoading] = useState(false)
  const [applyToOthersSelected, setApplyToOthersSelected] = useState([])

  const handleApplyToOtherEmployees = useCallback(async () => {
    const values = await form.validateFields().catch(() => null)
    if (!values || !values.employeeId || !applyToOthersSelected.length) return
    const currentEmployeeId = values.employeeId
    const payload = {
      tiers: (values.tiers || []).map((t) => ({
        threshold: Number(t.threshold) || 0,
        rate: Number(t.rate) || 0
      })),
      deductMaterialCost: !!values.deductMaterialCost,
      tierPerformanceItems: {
        service: true,
        product: values.includeProduct !== false,
        valueAdded: values.includeValueAdded !== false,
        classTicket: values.includeClassTicket === true
      },
      // 相容舊欄位：產品或儲值任一計入即為 true
      ladderIncludesProduct:
        values.includeProduct !== false || values.includeValueAdded !== false,
      commissionRule:
        values.commissionRule === 'profit_rate_minus_fee'
          ? 'profit_rate_minus_fee'
          : 'sales_rate'
    }
    if (payload.deductMaterialCost && values.materialCostType != null) {
      payload.materialCostDefault = {
        type: values.materialCostType,
        ...(values.materialCostType === 'amount'
          ? { amount: Number(values.materialCostValue) || 0 }
          : { percentage: Number(values.materialCostValue) || 0 })
      }
    }
    setApplyToOthersLoading(true)
    let ok = 0
    let err = 0
    for (const employeeId of applyToOthersSelected) {
      if (employeeId === currentEmployeeId) continue
      try {
        await api.put(
          `/api/businesses/${businessId}/employees/${employeeId}/commission`,
          payload
        )
        ok += 1
      } catch (e) {
        err += 1
      }
    }
    setApplyToOthersLoading(false)
    setApplyToOthersVisible(false)
    setApplyToOthersSelected([])
    if (ok > 0) message.success(`已套用到 ${ok} 位員工`)
    if (err > 0) message.warning(`${err} 位員工套用失敗，請稍後再試`)
  }, [businessId, form, applyToOthersSelected])

  const openApplyToOthersModal = useCallback(() => {
    const currentId = form.getFieldValue('employeeId')
    if (!currentId) {
      message.warning('請先選擇一位員工並保存設定後，再使用套用到其他員工')
      return
    }
    setApplyToOthersSelected([])
    setApplyToOthersVisible(true)
  }, [form])

  // 權限檢查：只有品牌方案才能使用此功能
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('salary')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要品牌方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  const handleReset = () => {
    form.resetFields()
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (!businessId) {
        message.error('未能取得商家資訊，請重新登入後再試')
        return
      }
      if (!values.tiers || values.tiers.length < MIN_TIERS) {
        message.error('請至少新增一組抽成設定')
        return
      }

      setSaving(true)
      const payload = {
        tiers: values.tiers.map((tier) => ({
          threshold: Number(tier.threshold) || 0,
          rate: Number(tier.rate) || 0
        }))
      }
      if (values.deductMaterialCost !== undefined) {
        payload.deductMaterialCost = !!values.deductMaterialCost
      }
      if (payload.deductMaterialCost && values.materialCostType != null) {
        payload.materialCostDefault = {
          type: values.materialCostType,
          ...(values.materialCostType === 'amount'
            ? { amount: Number(values.materialCostValue) || 0 }
            : { percentage: Number(values.materialCostValue) || 0 })
        }
      }
      payload.tierPerformanceItems = {
        service: true,
        product: values.includeProduct !== false,
        valueAdded: values.includeValueAdded !== false,
        classTicket: values.includeClassTicket === true
      }
      // 相容舊欄位：產品或儲值任一計入即為 true
      payload.ladderIncludesProduct =
        values.includeProduct !== false || values.includeValueAdded !== false
      payload.commissionRule =
        values.commissionRule === 'profit_rate_minus_fee'
          ? 'profit_rate_minus_fee'
          : 'sales_rate'
      // 個人堂票抽成：空白 = 沿用全店堂票抽成設定
      payload.classTicketRate =
        values.classTicketRate === null ||
        values.classTicketRate === undefined ||
        values.classTicketRate === ''
          ? null
          : Number(values.classTicketRate)

      await api.put(
        `/api/businesses/${businessId}/employees/${values.employeeId}/commission`,
        payload
      )
      message.success('業績抽成設定已更新')

      // 提示用戶是否需要重新計算當月薪資
      Modal.confirm({
        title: '抽成規則已更新',
        content:
          '抽成規則已更新，是否重新計算當月薪資？重新計算會使用最新的抽成規則更新所有員工的薪資數據。',
        okText: '重新計算',
        cancelText: '稍後再說',
        onOk: async () => {
          try {
            // ⚠️ 原本是 new Date().toISOString()，那是 UTC —— 台灣時間每月 1 號
            // 08:00 以前都會算成上個月的薪資。
            const currentMonth = tzNow().format('YYYY-MM')
            message.loading('正在重新計算薪資，請稍候...', 0)
            await api.get(
              `/api/businesses/${businessId}/commission/monthly-salary`,
              {
                params: {
                  month: currentMonth,
                  recalculate: 'true'
                }
              }
            )
            message.destroy()
            message.success('重新計算完成！薪資數據已更新')
          } catch (error) {
            message.destroy()
            console.error('重新計算失敗:', error)
            message.error(
              error.response?.data?.message ||
                error.message ||
                '重新計算失敗，請稍後再試'
            )
          }
        }
      })
    } catch (error) {
      if (error?.errorFields) {
        return
      }
      console.error('更新抽成設定失敗', error)
      message.error(
        error.response?.data?.message ||
          error.message ||
          '更新抽成設定失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard
          title={
            <Header>
              <Title level={3} style={{ marginBottom: 0, color: '#0f2562' }}>
                技術服務業績抽成設定
              </Title>
              <span>設置不同業績門檻對應的抽成比例，協助激勵團隊績效。</span>
            </Header>
          }
        >
          <Paragraph style={{ color: '#576079', marginBottom: 16 }}>
            <Button
              type='link'
              onClick={() => startCommissionTour(navigate, 0)}
              style={{ padding: 0, marginRight: 8 }}
            >
              觀看抽成設定教學
            </Button>
          </Paragraph>
          <Paragraph style={{ color: '#576079', marginBottom: 24 }}>
            選擇員工後，即可依序設定「大於等於（門檻）」與「抽成趴數」。
            值不得為負數，如需更多層級，可新增多組設定。
            <br />
            <Text type='secondary' style={{ fontSize: 12 }}>
              💡 優先順序：若該筆服務已設定「服務項目抽成規則」，以服務規則為準；未設定時才套用此處的員工階梯抽成（含是否先扣材料費）。
            </Text>
          </Paragraph>

          <FormWrapper
            layout='vertical'
            form={form}
            initialValues={{
              employeeId: undefined,
              deductMaterialCost: false,
              materialCostType: 'percentage',
              materialCostValue: 0,
              tiers: undefined
            }}
          >
            <div data-tour='commission-employee-select'>
              <Form.Item
                name='employeeId'
                label='選擇員工'
                rules={[{ required: true, message: '請選擇員工' }]}
              >
                <Select
                placeholder='請選擇員工'
                size='large'
                loading={loadingEmployees}
                onChange={handleEmployeeChange}
                showSearch
                optionFilterProp='label'
              >
                {employeeOptions.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.searchText}
                  >
                    {option.node}
                  </Select.Option>
                ))}
              </Select>
              </Form.Item>
            </div>

            <div data-tour='commission-deduct-material'>
              <Form.Item
                name='deductMaterialCost'
                label='抽成計算方式'
                valuePropName='checked'
                tooltip='僅在「該服務沒有服務項目抽成規則」時生效。開啟後：抽成基礎 = 服務業績 − 材料費，再依上方階梯趴數計算。若服務已有服務項目抽成規則，以該規則為準，此項不套用。'
              >
                <Switch
                  checkedChildren='先扣材料費再算抽成'
                  unCheckedChildren='不扣材料費'
                />
              </Form.Item>
            </div>

            <Form.Item
              label='總業績計入項目'
              tooltip='勾選哪些業績要合併計入「總業績」。總業績同時作為階梯門檻判定的基數，會影響達到的抽成階梯。技術業績為必計基底，無法取消。'
              style={{ marginBottom: 8 }}
            >
              <div style={{ marginBottom: 8 }}>
                <Checkbox checked disabled>
                  技術業績（設計師主操作，必計）
                </Checkbox>
              </div>
              <Form.Item
                name='includeProduct'
                valuePropName='checked'
                initialValue={true}
                noStyle
              >
                <Checkbox>產品業績</Checkbox>
              </Form.Item>
              <div style={{ height: 8 }} />
              <Form.Item
                name='includeValueAdded'
                valuePropName='checked'
                initialValue={true}
                noStyle
              >
                <Checkbox>儲值業績</Checkbox>
              </Form.Item>
              <div style={{ height: 8 }} />
              <Form.Item
                name='includeClassTicket'
                valuePropName='checked'
                initialValue={false}
                noStyle
              >
                <Checkbox>堂票業績</Checkbox>
              </Form.Item>
            </Form.Item>
            <div style={{ marginTop: 0, marginBottom: 16 }}>
              <Text type='secondary' style={{ fontSize: 12 }}>
                勾選的項目會加總為「總業績」，並用來判定業績階梯（進而影響技術抽成%）。各類別自身的抽成仍依其各自的抽成設定計算，此處只決定是否併入總業績。
              </Text>
            </div>

            {/* ── 個人堂票抽成 ── */}
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 12 }}>
              個人堂票抽成
            </div>
            <Form.Item
              name='classTicketRate'
              initialValue={null}
              extra='這位員工賣出的堂票，依售價抽這個比例。留空 = 沿用「堂票銷售抽成設定」的全店階梯。'
              style={{ marginBottom: 24 }}
            >
              <InputNumber
                min={0}
                max={100}
                addonAfter='%'
                placeholder='沿用全店設定'
                style={{ width: 200 }}
              />
            </Form.Item>

            {/* ── 計算規則 ── */}
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e', marginBottom: 12 }}>
              計算規則
            </div>
            <Form.Item
              name='commissionRule'
              initialValue='sales_rate'
              style={{ marginBottom: 24 }}
            >
              <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Radio value='sales_rate' style={{ padding: '14px 16px', border: '1px solid #eef0f5', borderRadius: 12, background: '#fff', width: '100%', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginLeft: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e' }}>
                      按業績抽成（不扣手續費）
                    </span>
                    <code style={{ fontSize: 13, color: '#6b7280', background: '#f4f5f7', padding: '4px 8px', borderRadius: 6 }}>
                      抽成 = 服務金額 × 當階抽成%
                    </code>
                    <span style={{ fontSize: 12, color: '#8e8e93' }}>
                      不論支付方式為何，統一以服務金額乘以抽成比例計算。
                    </span>
                  </div>
                </Radio>
                <Radio value='profit_rate_minus_fee' style={{ padding: '14px 16px', border: '1px solid #eef0f5', borderRadius: 12, background: '#fff', width: '100%', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginLeft: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1c1e' }}>
                      按業績抽成並扣除手續費（刷卡才扣）
                    </span>
                    <code style={{ fontSize: 13, color: '#6b7280', background: '#f4f5f7', padding: '4px 8px', borderRadius: 6 }}>
                      抽成 = 服務金額 × 當階抽成% − 分攤金流手續費
                    </code>
                    <span style={{ fontSize: 12, color: '#8e8e93' }}>
                      • 客戶以<b>刷卡</b>或<b>行動支付</b>付款時，按服務金額比例分攤整單手續費。
                      <br />
                      • 現金、轉帳付款時手續費為 0，與規則一相同。
                      <br />
                      • 手續費率在「金流手續費設定」中統一設定。
                    </span>
                  </div>
                </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, next) =>
                prev.deductMaterialCost !== next.deductMaterialCost ||
                prev.materialCostType !== next.materialCostType
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('deductMaterialCost') ? (
                  <MaterialCostBlock>
                    <Form.Item
                      name='materialCostType'
                      label='材料費預設（無服務規則時）'
                      tooltip='此設定套用於所有「未設定服務項目抽成規則」的服務。每筆該類服務會先扣除此處設定的材料費，再以餘額 × 階梯趴數計算抽成。'
                      rules={[{ required: true }]}
                    >
                      <Radio.Group>
                        <Radio value='percentage'>比例（%）</Radio>
                        <Radio value='amount'>固定金額（NT$）</Radio>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item
                      name='materialCostValue'
                      label={getFieldValue('materialCostType') === 'amount' ? '扣除金額（NT$）' : '扣除比例（%）'}
                      rules={[
                        { required: true, message: '請填寫材料費' },
                        {
                          type: 'number',
                          min: 0,
                          message: '不得小於 0'
                        }
                      ]}
                    >
                      <InputNumber
                        min={0}
                        max={getFieldValue('materialCostType') === 'percentage' ? 100 : undefined}
                        placeholder={getFieldValue('materialCostType') === 'amount' ? '例如 50' : '例如 10'}
                        style={{ width: 160 }}
                        addonAfter={getFieldValue('materialCostType') === 'percentage' ? '%' : 'NT$'}
                      />
                    </Form.Item>
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      套用於：所有「未設定服務項目抽成規則」的服務項目。
                    </Text>
                  </MaterialCostBlock>
                ) : null
              }
            </Form.Item>

            <Form.Item
              shouldUpdate={(prev, next) =>
                prev.employeeId !== next.employeeId || prev.tiers !== next.tiers
              }
            >
              {({ getFieldValue }) => {
                const selectedEmployee = getFieldValue('employeeId')
                if (!selectedEmployee) {
                  return (
                    <EmptyHint>請先選擇員工，再設定業績抽成規則。</EmptyHint>
                  )
                }

                return (
                  <div data-tour='commission-tiers'>
                  <Form.List
                    name='tiers'
                    initialValue={[{ threshold: 0, rate: 0 }]}
                  >
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, index) => (
                          <TierRow key={field.key}>
                            <Space direction='horizontal' size={12} wrap>
                              <LabelText>大於等於 ≥</LabelText>
                              <Form.Item
                                {...field}
                                name={[field.name, 'threshold']}
                                fieldKey={[field.fieldKey, 'threshold']}
                                rules={[
                                  { required: true, message: '請輸入門檻金額' },
                                  {
                                    type: 'number',
                                    min: 0,
                                    message: '門檻金額不得小於 0'
                                  }
                                ]}
                              >
                                <InputNumber
                                  min={0}
                                  placeholder='輸入金額'
                                  style={{ width: 160 }}
                                  formatter={(value) =>
                                    value === undefined || value === null
                                      ? ''
                                      : `${Number(value)
                                          .toString()
                                          .replace(
                                            /\B(?=(\d{3})+(?!\d))/g,
                                            ','
                                          )}`
                                  }
                                  parser={(value) =>
                                    value
                                      ? Number(value.replace(/[^\d.]/g, ''))
                                      : 0
                                  }
                                />
                              </Form.Item>
                            </Space>

                            <Space direction='horizontal' size={12} wrap>
                              <LabelText>抽成趴數</LabelText>
                              <Form.Item
                                {...field}
                                name={[field.name, 'rate']}
                                fieldKey={[field.fieldKey, 'rate']}
                                rules={[
                                  { required: true, message: '請輸入抽成趴數' },
                                  {
                                    type: 'number',
                                    min: 0,
                                    message: '抽成趴數不得小於 0'
                                  }
                                ]}
                              >
                                <InputNumber
                                  min={0}
                                  max={1000}
                                  placeholder='輸入趴數'
                                  style={{ width: 140 }}
                                  formatter={(value) =>
                                    value === undefined || value === null
                                      ? ''
                                      : `${value}`.replace(
                                          /\B(?=(\d{3})+(?!\d))/g,
                                          ','
                                        )
                                  }
                                />
                              </Form.Item>
                              <Text style={{ color: '#8c8c8c' }}>%</Text>
                            </Space>

                            {fields.length > MIN_TIERS && (
                              <Button
                                type='text'
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                              >
                                移除
                              </Button>
                            )}
                          </TierRow>
                        ))}

                        <Button
                          type='dashed'
                          onClick={() => add({ threshold: 0, rate: 0 })}
                          block
                          icon={<PlusOutlined />}
                          style={{
                            borderStyle: 'dashed',
                            marginTop: 12,
                            height: 48
                          }}
                        >
                          新增抽成階梯
                         </Button>
                      </>
                    )}
                  </Form.List>
                  </div>
                )
              }}
            </Form.Item>

            <Actions>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                清除
              </Button>
              <Button
                data-tour='commission-save'
                type='primary'
                icon={<SaveOutlined />}
                onClick={handleSubmit}
                loading={saving}
                disabled={fetchingCommission || loadingEmployees}
              >
                保存設定
              </Button>
              <Button
                icon={<CopyOutlined />}
                onClick={openApplyToOthersModal}
                disabled={fetchingCommission || loadingEmployees || !form.getFieldValue('employeeId')}
              >
                套用到其他員工
              </Button>
            </Actions>
          </FormWrapper>

          <Modal
            title='套用到其他員工'
            open={applyToOthersVisible}
            onCancel={() => {
              setApplyToOthersVisible(false)
              setApplyToOthersSelected([])
            }}
            onOk={handleApplyToOtherEmployees}
            okText='套用'
            confirmLoading={applyToOthersLoading}
            destroyOnClose
          >
            <Paragraph style={{ marginBottom: 12 }}>
              將目前畫面上的設定（階梯、先扣材料費、材料費預設）一鍵套用到以下員工。已選擇的員工不會被覆蓋。
            </Paragraph>
            <Form.Item label='選擇員工'>
              <Select
                mode='multiple'
                placeholder='請選擇要套用的員工'
                value={applyToOthersSelected}
                onChange={setApplyToOthersSelected}
                style={{ width: '100%' }}
                optionFilterProp='label'
                options={employeeOptions
                  .filter((opt) => opt.value !== form.getFieldValue('employeeId'))
                  .map((opt) => ({ value: opt.value, label: opt.searchText }))}
              />
            </Form.Item>
            {applyToOthersSelected.length > 0 && (
              <Text type='secondary'>
                已選 {applyToOthersSelected.length} 位，點擊「套用」後將更新其抽成設定。
              </Text>
            )}
          </Modal>

          {fetchingCommission && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <Spin size='small' tip='載入抽成設定中...' />
            </div>
          )}
        </StyledCard>

        <InfoCard>
          <Title level={5} style={{ color: '#0f2562', marginBottom: 12 }}>
            設定建議
          </Title>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              • 建議由低至高設定門檻，付款金額達標後套用對應抽成比例。
            </Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              • 抽成比例請依照公司政策設定，若需設定固定額度，可留空或填 0。
            </Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              • 「先扣材料費再算抽成」僅在該服務沒有服務項目抽成規則時套用，公式：(服務業績 − 材料費) × 本頁階梯趴數；材料費由此頁「材料費預設」設定（比例或固定金額），套用於所有未設服務規則的服務。
            </Text>
          </Paragraph>
          <Paragraph>
            <Text strong>
              • 如需更進階的計算方式，可於系統外補充備註或另行處理。
            </Text>
          </Paragraph>
        </InfoCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default EmployeeCommissionSettingsPage
